import { ref, computed } from 'vue'
import { usePage } from '@inertiajs/vue3'

// Debounced (300ms) server-side price quote for a public listing —
// replaces the old client-only "days × rate" math with a real
// PriceQuote.for(...) call (GET /quote, see Public::QuotesController) so a
// rate-plan tier is always reflected and a customer never sees a number
// the server itself wouldn't also charge at checkout. `schedule(params)`
// is a no-op-safe function the caller invokes on every relevant input
// change (dates, party size); pass no params (or don't call it) to leave
// `quote` at its last value / null. `loading` is true only for the actual
// in-flight window — callers should fall back to their old local-math
// display while it's true (and once, before any quote has ever landed).
export function usePriceQuote(type, id) {
  const page = usePage()
  const quotePath = computed(() => page.props.quote_path || '/quote')

  const quote   = ref(null)
  const loading = ref(false)

  let timer = null
  let controller = null

  function schedule(params) {
    clearTimeout(timer)
    if (!params) { quote.value = null; return }
    timer = setTimeout(() => fetchQuote(params), 300)
  }

  async function fetchQuote(params) {
    controller?.abort()
    controller = new AbortController()
    // Capture THIS call's controller. `controller` (the closure variable) can
    // be reassigned by a later, overlapping fetchQuote before this call's
    // await settles — comparing against `mine` is how a superseded call
    // recognizes itself and refuses to touch shared state (quote/loading)
    // on the way out, even though its abort landed asynchronously rather
    // than synchronously.
    const mine = controller
    loading.value = true
    try {
      const qs = new URLSearchParams({ type, id: String(id), ...params })
      const res = await fetch(`${quotePath.value}?${qs.toString()}`, {
        signal: mine.signal,
        headers: { Accept: 'application/json' }
      })
      const body = res.ok ? await res.json() : null
      if (mine !== controller) return // superseded while the response/body was in flight
      quote.value = body
    } catch (e) {
      if (mine !== controller) return // superseded — a stale abort/error must never touch shared state
      if (e?.name !== 'AbortError') quote.value = null
    } finally {
      if (mine === controller) loading.value = false
    }
  }

  return { quote, loading, schedule }
}
